import { PasswordFormField } from '@/components/shared/PasswordFormField/PasswordFormField';
import { validatePassword } from '@/components/shared/PasswordFormField/validation';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/Form/Form';
import { Input } from '@/components/ui/Input/Input';
import { PasswordInput } from '@/components/ui/PasswordInput/PasswordInput';
import { toast } from '@/components/ui/Toast/Toast';
import { paths } from '@/config/paths';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import { isApiError } from '@/lib/apiClient';
import { hasNoLeadingOrTrailingSpaces } from '@/utils/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { useRegister } from '../../api/register';

interface RegistrationFormProps {
  username?: string;
  invitationToken: string;
}

export const RegistrationForm = ({ username = '', invitationToken }: RegistrationFormProps) => {
  const { t } = useTranslation('auth', {
    keyPrefix: 'register.accountSetupStep',
  });

  const { t: tValidation } = useTranslation('utils', {
    keyPrefix: 'validation',
  });

  const navigate = useNavigate();

  const { translateError } = useErrorTranslation();

  const { mutate } = useRegister({
    options: {
      onError: (error) => {
        // The mutation rejects with the raw AxiosError, so the API's error codes sit
        // in the response body, not on the error itself.
        const apiErrors = isApiError(error) ? error.response?.data?.errors : error?.errors;
        if (apiErrors?.some((err) => err.code === 'USERNAME_TAKEN' || err.code === 'USERNAME_INVALID')) {
          form.setError('username', {
            type: 'custom',
            message: translateError(error),
          });
        } else {
          toast({
            variant: 'destructive',
            description: translateError(error),
          });
        }
      },
      onSuccess: () => {
        void navigate(`${paths.auth.register.path}?success=true`, { replace: true });
      },
    },
  });

  const formSchema = z
    .object({
      username: z
        .string()
        .min(5, {
          message: tValidation('lengthRange', {
            field: t('username.label'),
            min: 5,
            max: 128,
          }),
        })
        .max(128, {
          message: tValidation('lengthRange', {
            field: t('username.label'),
            min: 5,
            max: 128,
          }),
        })
        .refine((value) => !value.includes(' '), { message: tValidation('noSpace') })
        // Matches the character set the API accepts: letters (accents included),
        // digits and . _ - only.
        .refine((value) => /^[\p{L}\p{M}\p{N}._-]+$/u.test(value), {
          message: tValidation('usernameFormat'),
        }),
      password: z
        .string()
        .refine((val) => validatePassword(val).isValid, {
          message: tValidation('passwordValidation'),
        })
        .refine((val) => hasNoLeadingOrTrailingSpaces(val), {
          message: tValidation('noLeadingAndTrailingSpaces'),
        }),
      repeatPassword: z.string().min(1, {
        message: tValidation('required'),
      }),
    })
    .superRefine((data, ctx) => {
      if (data.password !== data.repeatPassword) {
        ctx.addIssue({
          code: 'custom',
          message: tValidation('passwordMismatch'),
          path: ['repeatPassword'],
        });
      }
    });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      /* eslint-disable unicorn/prefer-string-replace-all */
      username: username.toLowerCase().replace(/\s/g, ''),
      password: '',
      repeatPassword: '',
    },
  });

  const password = form.watch('password');
  const repeatPassword = form.watch('repeatPassword');
  useEffect(() => {
    if (form.formState.submitCount > 0) {
      void form.trigger('repeatPassword');
    }
  }, [password, repeatPassword, form]);

  const onSubmit = (values: z.infer<typeof formSchema>) =>
    mutate({
      username: values.username.toLowerCase(),
      password: values.password,
      invitationToken,
    });

  return (
    <Form {...form}>
      <form
        id='account-setup-form'
        onSubmit={(e) => {
          void form.handleSubmit(onSubmit)(e);
        }}
        className='flex flex-col gap-4'
      >
        <FormField
          control={form.control}
          name='username'
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>{t('username.label')}</FormLabel>
                <FormControl>
                  <Input {...field} autoComplete='username' />
                </FormControl>
                <FormDescription>{t('username.description')}</FormDescription>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <PasswordFormField control={form.control} name='password' autoComplete='new-password' />
        <FormField
          control={form.control}
          name='repeatPassword'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('repeatPassword.label')}</FormLabel>
              <FormControl>
                <PasswordInput {...field} autoComplete='new-password' />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};
